import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { ErrorMessage, HelpText, Input } from '#components/atoms/forms/index.ts';
import { ConfigOverrideHelpText } from '#components/patterns/config-overrides/help-text/ConfigOverrideHelpText.tsx';
import {
  CONFIG_OVERRIDE_LABELS,
  LOGGING_FORMAT_LABELS,
  STATIC_MAP_NETWORK_LABELS,
  STATS_TYPE_LABELS,
} from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { UnknownConfigOverridesList } from '#components/patterns/config-overrides/unknown/UnknownConfigOverridesList.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { FieldSet } from '#components/patterns/forms/fieldset/FieldSet.tsx';
import { OptionWithHint } from '#components/patterns/forms/select/option/OptionWithHint.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import type { ConfigOverride, ConfigOverrideKey } from '#dn-types/host.ts';
import {
  LOGGING_FORMAT,
  LOGGING_LEVEL,
  LOGGING_TIMESTAMP_ITEMS,
  ON_OFF,
  STATIC_MAP_NETWORK,
  STATS_TYPE,
  TRUE_FALSE,
} from '#schemas/config-override/helpers/enums.ts';
import type { UnknownConfigOverride } from '#util/unknownConfigOverrides.ts';
import { setValueAsNumberOrNull } from '#util/validation/set-value-as-number-or-null.ts';
import { makeUniqueCIDRRequiredValidator } from '#util/validation/validators/uniqueCIDRRequired.ts';
import { makeUniqueNameRequiredValidator } from '#util/validation/validators/uniqueNameRequired.ts';
import type { OverridePlusSource } from '../../hooks/useOverrideMaps.ts';
import type { HostFormData } from '../host-form-schema.ts';
import { AllowList } from './allow-list/AllowList.tsx';
import { FieldFooter } from './field-footer/FieldFooter.tsx';
import { LocalAllowModal } from './local-allow/LocalAllowModal.tsx';
import { RemoteAllowModal } from './remote-allow/RemoteAllowModal.tsx';
import { ResetButton } from './reset-button/ResetButton.tsx';
import styles from './HostAdvancedFormPart.module.css';

type Props = {
  hostType: 'lighthouse' | 'host' | 'relay';
  isLoading?: boolean;
  overrideMap: Map<ConfigOverrideKey, OverridePlusSource>;
  hostOverrides: ConfigOverride[];
  clearHostOverride: (key: ConfigOverrideKey) => void;
  /**
   * The host's overrides with no editor here, including ones pending delete; rendered read-only
   * with delete/restore as the only actions
   */
  unknownOverrides: UnknownConfigOverride[];
  /** Indices into `unknownOverrides` that are pending delete */
  removedUnknownIndices: ReadonlySet<number>;
  removeUnknownOverride: (index: number) => void;
  restoreUnknownOverride: (index: number) => void;
};

/**
 * A form to configure advanced nebula settings.
 *
 * NOTE: When adding / removing fields from this config form, be sure to also update:
 *
 * - mapFormToOverrides
 * - mapOverridesToForm
 * - RESTART_REQUIRED_SETTINGS
 * - mapApiErrorsToForm
 */
export function HostAdvancedFormPart({
  hostType,
  isLoading,
  overrideMap,
  hostOverrides,
  clearHostOverride,
  unknownOverrides,
  removedUnknownIndices,
  removeUnknownOverride,
  restoreUnknownOverride,
}: Props) {
  const formContext = useFormContext<HostFormData>();
  const { control, register, formState, setValue } = formContext;
  const {
    fields: lighthouseRemoteAllows,
    remove: removeRemoteAllow,
    append: appendRemoteAllow,
    update: updateRemoteAllow,
    replace: replaceRemoteAllow,
  } = useFieldArray({
    control,
    name: 'configOverrides.lighthouse.remote_allow_list',
  });
  const {
    fields: lighthouseLocalAllows,
    remove: removeLocalAllow,
    append: appendLocalAllow,
    update: updateLocalAllow,
    replace: replaceLocalAllow,
  } = useFieldArray({
    control,
    name: 'configOverrides.lighthouse.local_allow_list',
  });
  const [isAddingRemoteAllow, setAddingRemoteAllow] = useState<boolean>(false);
  const [isAddingLocalAllow, setAddingLocalAllow] = useState<boolean>(false);
  const [editRemoteAllowIndex, setEditRemoteAllowIndex] = useState<number | null>(null);
  const [editLocalAllowIndex, setEditLocalAllowIndex] = useState<number | null>(null);
  const remoteAllowButtonRef = useRef<HTMLButtonElement>(null);
  const localAllowButtonRef = useRef<HTMLButtonElement>(null);
  const { data: featureFlags } = useSuspenseFeatureFlagsQuery();

  const { errors } = formState;
  const [statsType, listenPort, serveDNS, remoteAllowList, localAllowList] = useWatch({
    control,
    name: [
      'configOverrides.stats.type',
      'listenPort',
      'configOverrides.lighthouse.serve_dns',
      'configOverrides.lighthouse.remote_allow_list',
      'configOverrides.lighthouse.local_allow_list',
    ],
  });
  const currentRemoteCIDRs = remoteAllowList.map((a) => a.cidr);
  const currentLocalCIDRs = localAllowList.map((a) => ('cidr' in a ? a.cidr : a.interface));

  // Focus lighthouse.remoteAllowList button if it has an error on submission
  useEffect(() => {
    if (formState.isSubmitted && !formState.isSubmitSuccessful) {
      if (formState.errors.configOverrides?.lighthouse?.remote_allow_list && remoteAllowButtonRef.current) {
        remoteAllowButtonRef.current.focus();
      } else if (formState.errors.configOverrides?.lighthouse?.local_allow_list && localAllowButtonRef.current) {
        localAllowButtonRef.current.focus();
      }
    }
  }, [formState]);

  return (
    <>
      <div className={`mb-24 ${styles.HostAdvancedFormPart_grid}`}>
        <FieldSet legend="IP preferences">
          <Field label={CONFIG_OVERRIDE_LABELS['lighthouse.remote_allow_list']}>
            <ConfigOverrideHelpText configOverride="lighthouse.remote_allow_list" />
            <AllowList
              name="configOverrides.lighthouse.remote_allow_list"
              items={lighthouseRemoteAllows}
              addButtonProps={{ children: 'Add remote allow rule', disabled: Boolean(isLoading) }}
              addButtonRef={remoteAllowButtonRef}
              onAdd={() => {
                setAddingRemoteAllow(true);
              }}
              onEdit={(index) => {
                setEditRemoteAllowIndex(index);
              }}
              onRemove={removeRemoteAllow}
              onReset={(values) => replaceRemoteAllow(values)}
              overrideMap={overrideMap}
            />
            {errors.configOverrides?.lighthouse?.remote_allow_list && (
              <ErrorMessage>{errors.configOverrides.lighthouse.remote_allow_list.message}</ErrorMessage>
            )}
            {errors.configOverrides?.lighthouse?.remote_allow_list?.root && (
              <ErrorMessage>{errors.configOverrides.lighthouse.remote_allow_list.root.message}</ErrorMessage>
            )}
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS['lighthouse.local_allow_list']}>
            <ConfigOverrideHelpText configOverride="lighthouse.local_allow_list" />
            <AllowList
              name="configOverrides.lighthouse.local_allow_list"
              items={lighthouseLocalAllows}
              addButtonProps={{ children: 'Add local allow rule', disabled: Boolean(isLoading) }}
              addButtonRef={localAllowButtonRef}
              onAdd={() => {
                setAddingLocalAllow(true);
              }}
              onEdit={(index) => {
                setEditLocalAllowIndex(index);
              }}
              onRemove={removeLocalAllow}
              onReset={(values) => {
                replaceLocalAllow(values);
              }}
              overrideMap={overrideMap}
            />
            {errors.configOverrides?.lighthouse?.local_allow_list && (
              <ErrorMessage>{errors.configOverrides.lighthouse.local_allow_list.message}</ErrorMessage>
            )}
            {errors.configOverrides?.lighthouse?.local_allow_list?.root && (
              <ErrorMessage>{errors.configOverrides.lighthouse.local_allow_list.root.message}</ErrorMessage>
            )}
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS['lighthouse.advertise_addrs']}>
            <ConfigOverrideHelpText configOverride="lighthouse.advertise_addrs" />
            <Input
              {...register('configOverrides.lighthouse.advertise_addrs', { disabled: isLoading })}
              className="width-100"
              hasError={Boolean(errors.configOverrides?.lighthouse?.advertise_addrs)}
            />
            <FieldFooter
              name="configOverrides.lighthouse.advertise_addrs"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS.preferred_ranges}>
            <ConfigOverrideHelpText configOverride="preferred_ranges" />
            <Input
              {...register('configOverrides.preferred_ranges', { disabled: isLoading })}
              className="width-100"
              hasError={Boolean(errors.configOverrides?.preferred_ranges)}
            />
            <FieldFooter
              name="configOverrides.preferred_ranges"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS['listen.host']}>
            <ConfigOverrideHelpText configOverride="listen.host" hostType={hostType} />
            <Input
              {...register('configOverrides.listen.host', { disabled: isLoading })}
              className="width-100"
              hasError={Boolean(errors.configOverrides?.listen?.host)}
            />
            <FieldFooter
              name="configOverrides.listen.host"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <SelectControlled
            label={CONFIG_OVERRIDE_LABELS['static_map.network']}
            name="configOverrides.static_map.network"
            control={control}
            disabled={Boolean(isLoading)}
            hasError={Boolean(errors.configOverrides?.static_map?.network)}
            fieldChildren={
              <FieldFooter
                name="configOverrides.static_map.network"
                overrideMap={overrideMap}
                hostOverrides={hostOverrides}
                clearOverride={clearHostOverride}
              />
            }
            renderValue={(item) => (item ? STATIC_MAP_NETWORK_LABELS[item] : null)}
            renderOptions={({ onChange }) => (
              <>
                {STATIC_MAP_NETWORK.map((v) => (
                  <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                    {STATIC_MAP_NETWORK_LABELS[v]}
                  </SelectItem>
                ))}
              </>
            )}
            help={<ConfigOverrideHelpText configOverride="static_map.network" />}
          />
          {hostType === 'host' ?
            <Field label="Listen port">
              {/** Not a true config override, so {@link ConfigOverrideHelpText} doesn't support it. */}
              <HelpText>
                <a href="https://nebula.defined.net/docs/config/listen/#listenport" className="a" target="_blank">
                  <code>listen.port</code>
                </a>{' '}
                is the UDP port nebula should use on a host. A value of “0” will dynamically assign a port number.
              </HelpText>
              <Input
                {...register('listenPort', {
                  setValueAs: setValueAsNumberOrNull,
                  disabled: isLoading,
                })}
                className="width-100"
                min={0}
                // 65536 is deliberately one above the real max UDP port: this form has no
                // noValidate, and 65535 would let native constraint validation block the
                // submit before the zod resolver can produce the styled max-port error.
                max={65536}
                hasError={Boolean(errors.listenPort)}
                type="number"
                inputMode="numeric"
              />
              <ResetButton
                onClick={() => {
                  setValue('listenPort', 0);
                }}
                isShown={listenPort !== 0}
              />
              {errors.listenPort?.message && <ErrorMessage className="mb-8">{errors.listenPort.message}</ErrorMessage>}
            </Field>
          : null}
          {hostType === 'lighthouse' && featureFlags.HOST_CONFIG_DNS ?
            <SelectControlled
              label={CONFIG_OVERRIDE_LABELS['lighthouse.serve_dns']}
              name="configOverrides.lighthouse.serve_dns"
              control={control}
              disabled={Boolean(isLoading)}
              help={<ConfigOverrideHelpText configOverride="lighthouse.serve_dns" />}
              hasError={Boolean(errors.configOverrides?.lighthouse?.serve_dns)}
              fieldChildren={
                <FieldFooter
                  name="configOverrides.lighthouse.serve_dns"
                  overrideMap={overrideMap}
                  hostOverrides={hostOverrides}
                  clearOverride={clearHostOverride}
                />
              }
              renderOptions={({ onChange }) => (
                <>
                  {ON_OFF.map((v) => (
                    <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                      {v}
                    </SelectItem>
                  ))}
                </>
              )}
            />
          : null}
          {serveDNS === 'On' ?
            <>
              <Field label={CONFIG_OVERRIDE_LABELS['lighthouse.dns.host']}>
                <ConfigOverrideHelpText configOverride="lighthouse.dns.host" />
                <Input
                  {...register('configOverrides.lighthouse.dns.host', { disabled: isLoading })}
                  className="width-100"
                  hasError={Boolean(errors.configOverrides?.lighthouse?.dns?.host)}
                />
                <FieldFooter
                  name="configOverrides.lighthouse.dns.host"
                  overrideMap={overrideMap}
                  hostOverrides={hostOverrides}
                  clearOverride={clearHostOverride}
                />
              </Field>
              <Field label={CONFIG_OVERRIDE_LABELS['lighthouse.dns.port']}>
                <ConfigOverrideHelpText configOverride="lighthouse.dns.port" />
                <Input
                  {...register('configOverrides.lighthouse.dns.port', {
                    setValueAs: setValueAsNumberOrNull,
                    disabled: isLoading,
                  })}
                  className="width-100"
                  type="number"
                  hasError={Boolean(errors.configOverrides?.lighthouse?.dns?.port)}
                />
                <FieldFooter
                  name="configOverrides.lighthouse.dns.port"
                  overrideMap={overrideMap}
                  hostOverrides={hostOverrides}
                  clearOverride={clearHostOverride}
                />
              </Field>
            </>
          : null}
        </FieldSet>
        <Divider />
        {featureFlags.DNS_CONFIG_OVERRIDES ?
          <>
            <FieldSet
              legend="DNS"
              helpText={
                <HelpText className="mb-8">
                  <code>definednet.dns</code> tells a host which DNS resolvers to use while its tunnel is up. Only
                  mobile devices apply these settings today — dnclient ignores them.
                </HelpText>
              }
            >
              <Field label={CONFIG_OVERRIDE_LABELS['definednet.dns.resolver_addrs']}>
                <ConfigOverrideHelpText configOverride="definednet.dns.resolver_addrs" />
                <Input
                  {...register('configOverrides.definednet.dns.resolver_addrs', { disabled: isLoading })}
                  className="width-100"
                  hasError={Boolean(errors.configOverrides?.definednet?.dns?.resolver_addrs)}
                />
                <FieldFooter
                  name="configOverrides.definednet.dns.resolver_addrs"
                  overrideMap={overrideMap}
                  hostOverrides={hostOverrides}
                  clearOverride={clearHostOverride}
                />
              </Field>
              <Field label={CONFIG_OVERRIDE_LABELS['definednet.dns.match_domains']}>
                <ConfigOverrideHelpText configOverride="definednet.dns.match_domains" />
                <Input
                  {...register('configOverrides.definednet.dns.match_domains', { disabled: isLoading })}
                  className="width-100"
                  hasError={Boolean(errors.configOverrides?.definednet?.dns?.match_domains)}
                />
                <FieldFooter
                  name="configOverrides.definednet.dns.match_domains"
                  overrideMap={overrideMap}
                  hostOverrides={hostOverrides}
                  clearOverride={clearHostOverride}
                />
              </Field>
              <Field label={CONFIG_OVERRIDE_LABELS['definednet.dns.search_domains']}>
                <ConfigOverrideHelpText configOverride="definednet.dns.search_domains" />
                <Input
                  {...register('configOverrides.definednet.dns.search_domains', { disabled: isLoading })}
                  className="width-100"
                  hasError={Boolean(errors.configOverrides?.definednet?.dns?.search_domains)}
                />
                <FieldFooter
                  name="configOverrides.definednet.dns.search_domains"
                  overrideMap={overrideMap}
                  hostOverrides={hostOverrides}
                  clearOverride={clearHostOverride}
                />
              </Field>
            </FieldSet>
            <Divider />
          </>
        : null}
        <FieldSet
          legend="Punchy"
          helpText={
            <HelpText className="mb-8">
              <a href="https://nebula.defined.net/docs/config/punchy/" className="a" target="_blank">
                <code>punchy</code>
              </a>{' '}
              controls how Nebula attempts to punch holes through NAT firewalls to establish direct connections. Values
              are durations, such as <code>1s</code> or <code>100ms</code>.
            </HelpText>
          }
        >
          <Field label={CONFIG_OVERRIDE_LABELS['punchy.delay']}>
            <ConfigOverrideHelpText configOverride="punchy.delay" />
            <Input
              {...register('configOverrides.punchy.delay', { disabled: isLoading })}
              className="width-100"
              hasError={Boolean(errors.configOverrides?.punchy?.delay)}
            />
            <FieldFooter
              name="configOverrides.punchy.delay"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS['punchy.respond_delay']}>
            <ConfigOverrideHelpText configOverride="punchy.respond_delay" />
            <Input
              {...register('configOverrides.punchy.respond_delay', { disabled: isLoading })}
              className="width-100"
              hasError={Boolean(errors.configOverrides?.punchy?.respond_delay)}
            />
            <FieldFooter
              name="configOverrides.punchy.respond_delay"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
        </FieldSet>
        <Divider />
        <FieldSet legend="TUN">
          <SelectControlled
            label={CONFIG_OVERRIDE_LABELS['tun.disabled']}
            name="configOverrides.tun.disabled"
            control={control}
            disabled={Boolean(isLoading)}
            help={<ConfigOverrideHelpText configOverride="tun.disabled" />}
            hasError={Boolean(errors.configOverrides?.tun?.disabled)}
            fieldChildren={
              <FieldFooter
                name="configOverrides.tun.disabled"
                overrideMap={overrideMap}
                hostOverrides={hostOverrides}
                clearOverride={clearHostOverride}
              />
            }
            renderValue={(item) => item || null}
            renderOptions={({ onChange }) => (
              <>
                {TRUE_FALSE.map((v) => (
                  <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                    {v}
                  </SelectItem>
                ))}
              </>
            )}
          />
          <Field label={CONFIG_OVERRIDE_LABELS['tun.dev']}>
            <ConfigOverrideHelpText configOverride="tun.dev" />
            <Input
              {...register('configOverrides.tun.dev', { disabled: isLoading })}
              className="width-100"
              hasError={Boolean(errors.configOverrides?.tun?.dev)}
            />
            <FieldFooter
              name="configOverrides.tun.dev"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS['tun.mtu']}>
            <ConfigOverrideHelpText configOverride="tun.mtu" />
            <Input
              {...register('configOverrides.tun.mtu', {
                setValueAs: setValueAsNumberOrNull,
                disabled: isLoading,
              })}
              className="width-100"
              type="number"
              hasError={Boolean(errors.configOverrides?.tun?.mtu)}
            />
            <FieldFooter
              name="configOverrides.tun.mtu"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS['tun.tx_queue']}>
            <ConfigOverrideHelpText configOverride="tun.tx_queue" />
            <Input
              {...register('configOverrides.tun.tx_queue', {
                setValueAs: setValueAsNumberOrNull,
                disabled: isLoading,
              })}
              className="width-100"
              type="number"
              hasError={Boolean(errors.configOverrides?.tun?.tx_queue)}
            />
            <FieldFooter
              name="configOverrides.tun.tx_queue"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
        </FieldSet>
        <Divider />
        <FieldSet legend="Performance tuning">
          <Field label={CONFIG_OVERRIDE_LABELS['listen.batch']}>
            <ConfigOverrideHelpText configOverride="listen.batch" />
            <Input
              {...register('configOverrides.listen.batch', {
                setValueAs: setValueAsNumberOrNull,
                disabled: isLoading,
              })}
              className="width-100"
              type="number"
              hasError={Boolean(errors.configOverrides?.listen?.batch)}
            />
            <FieldFooter
              name="configOverrides.listen.batch"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS['listen.read_buffer']}>
            <ConfigOverrideHelpText configOverride="listen.read_buffer" />
            <Input
              {...register('configOverrides.listen.read_buffer', {
                setValueAs: setValueAsNumberOrNull,
                disabled: isLoading,
              })}
              className="width-100"
              type="number"
              hasError={Boolean(errors.configOverrides?.listen?.read_buffer)}
            />
            <FieldFooter
              name="configOverrides.listen.read_buffer"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS['listen.write_buffer']}>
            <ConfigOverrideHelpText configOverride="listen.write_buffer" />
            <Input
              {...register('configOverrides.listen.write_buffer', {
                setValueAs: setValueAsNumberOrNull,
                disabled: isLoading,
              })}
              className="width-100"
              type="number"
              hasError={Boolean(errors.configOverrides?.listen?.write_buffer)}
            />
            <FieldFooter
              name="configOverrides.listen.write_buffer"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <Field label={CONFIG_OVERRIDE_LABELS.routines}>
            <ConfigOverrideHelpText configOverride="routines" />
            <Input
              {...register('configOverrides.routines', {
                setValueAs: setValueAsNumberOrNull,
                disabled: isLoading,
              })}
              className="width-100"
              type="number"
              hasError={Boolean(errors.configOverrides?.routines)}
            />
            <FieldFooter
              name="configOverrides.routines"
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearOverride={clearHostOverride}
            />
          </Field>
          <SelectControlled
            label={CONFIG_OVERRIDE_LABELS['listen.udp_offloads']}
            name="configOverrides.listen.udp_offloads"
            control={control}
            disabled={Boolean(isLoading)}
            help={<ConfigOverrideHelpText configOverride="listen.udp_offloads" />}
            hasError={Boolean(errors.configOverrides?.listen?.udp_offloads)}
            fieldChildren={
              <FieldFooter
                name="configOverrides.listen.udp_offloads"
                overrideMap={overrideMap}
                hostOverrides={hostOverrides}
                clearOverride={clearHostOverride}
              />
            }
            renderValue={(item) => item || null}
            renderOptions={({ onChange }) => (
              <>
                {TRUE_FALSE.map((v) => (
                  <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                    {v}
                  </SelectItem>
                ))}
              </>
            )}
          />
          <SelectControlled
            label={CONFIG_OVERRIDE_LABELS['tun.use_offloads']}
            name="configOverrides.tun.use_offloads"
            control={control}
            disabled={Boolean(isLoading)}
            help={<ConfigOverrideHelpText configOverride="tun.use_offloads" />}
            hasError={Boolean(errors.configOverrides?.tun?.use_offloads)}
            fieldChildren={
              <FieldFooter
                name="configOverrides.tun.use_offloads"
                overrideMap={overrideMap}
                hostOverrides={hostOverrides}
                clearOverride={clearHostOverride}
              />
            }
            renderValue={(item) => item || null}
            renderOptions={({ onChange }) => (
              <>
                {TRUE_FALSE.map((v) => (
                  <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                    {v}
                  </SelectItem>
                ))}
              </>
            )}
          />
        </FieldSet>
        <Divider />
        <FieldSet legend="Logging">
          <SelectControlled
            label={CONFIG_OVERRIDE_LABELS['logging.level']}
            name="configOverrides.logging.level"
            control={control}
            disabled={Boolean(isLoading)}
            hasError={Boolean(errors.configOverrides?.logging?.level)}
            fieldChildren={
              <FieldFooter
                name="configOverrides.logging.level"
                overrideMap={overrideMap}
                hostOverrides={hostOverrides}
                clearOverride={clearHostOverride}
              />
            }
            renderValue={(item) => (item ? `${item.charAt(0).toUpperCase()}${item.slice(1)}` : null)}
            renderOptions={({ onChange }) => (
              <>
                {LOGGING_LEVEL.map((v) => (
                  <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                    {`${v.charAt(0).toUpperCase()}${v.slice(1)}`}
                  </SelectItem>
                ))}
              </>
            )}
            help={<ConfigOverrideHelpText configOverride="logging.level" />}
          />
          <SelectControlled
            label={CONFIG_OVERRIDE_LABELS['logging.format']}
            name="configOverrides.logging.format"
            control={control}
            disabled={Boolean(isLoading)}
            hasError={Boolean(errors.configOverrides?.logging?.format)}
            fieldChildren={
              <FieldFooter
                name="configOverrides.logging.format"
                overrideMap={overrideMap}
                hostOverrides={hostOverrides}
                clearOverride={clearHostOverride}
              />
            }
            renderValue={(item) => (item ? LOGGING_FORMAT_LABELS[item] : null)}
            renderOptions={({ onChange }) => (
              <>
                {LOGGING_FORMAT.map((v) => (
                  <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                    {LOGGING_FORMAT_LABELS[v]}
                  </SelectItem>
                ))}
              </>
            )}
            help={<ConfigOverrideHelpText configOverride="logging.format" />}
          />
          <SelectControlled
            label={CONFIG_OVERRIDE_LABELS['logging.timestamp_format']}
            name="configOverrides.logging.timestamp_format"
            control={control}
            disabled={Boolean(isLoading)}
            hasError={Boolean(errors.configOverrides?.logging?.timestamp_format)}
            fieldChildren={
              <FieldFooter
                name="configOverrides.logging.timestamp_format"
                overrideMap={overrideMap}
                hostOverrides={hostOverrides}
                clearOverride={clearHostOverride}
              />
            }
            renderValue={(value) => value || null}
            renderOptions={({ onChange }) => (
              <>
                {LOGGING_TIMESTAMP_ITEMS.map((item) => (
                  <SelectItem key={item.label} value={item.label} onClick={() => onChange(item.label)}>
                    <OptionWithHint value={item.label} hint={item.value} />
                  </SelectItem>
                ))}
              </>
            )}
            help={<ConfigOverrideHelpText configOverride="logging.timestamp_format" />}
          />
        </FieldSet>
        <Divider />
        <FieldSet
          legend="Stats"
          helpText={
            <HelpText className="mb-8">
              <a href="https://nebula.defined.net/docs/config/stats/" className="a" target="_blank">
                <code>stats</code>
              </a>{' '}
              allows sending statistics for hosts to a stats consuming service.
            </HelpText>
          }
        >
          <SelectControlled
            label={CONFIG_OVERRIDE_LABELS['stats.type']}
            name="configOverrides.stats.type"
            control={control}
            disabled={Boolean(isLoading)}
            hasError={Boolean(errors.configOverrides?.stats?.type)}
            fieldChildren={
              <FieldFooter
                name="configOverrides.stats.type"
                overrideMap={overrideMap}
                hostOverrides={hostOverrides}
                clearOverride={clearHostOverride}
              />
            }
            renderValue={(item) => (item ? STATS_TYPE_LABELS[item] : null)}
            renderOptions={({ onChange }) => (
              <>
                {STATS_TYPE.map((v) => (
                  <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                    {STATS_TYPE_LABELS[v]}
                  </SelectItem>
                ))}
              </>
            )}
          />
          {statsType === 'prometheus' || statsType === 'graphite' ?
            <>
              {statsType === 'prometheus' ?
                <>
                  <Field label={CONFIG_OVERRIDE_LABELS['stats.listen']}>
                    <ConfigOverrideHelpText configOverride="stats.listen" />
                    <Input
                      {...register('configOverrides.stats.listen', { disabled: isLoading })}
                      className="width-100"
                      hasError={Boolean(errors.configOverrides?.stats?.listen)}
                    />
                    <FieldFooter
                      name="configOverrides.stats.listen"
                      overrideMap={overrideMap}
                      hostOverrides={hostOverrides}
                      clearOverride={clearHostOverride}
                    />
                  </Field>
                  <Field label={CONFIG_OVERRIDE_LABELS['stats.path']}>
                    <ConfigOverrideHelpText configOverride="stats.path" />
                    <Input
                      {...register('configOverrides.stats.path', { disabled: isLoading })}
                      className="width-100"
                      hasError={Boolean(errors.configOverrides?.stats?.path)}
                    />
                    <FieldFooter
                      name="configOverrides.stats.path"
                      overrideMap={overrideMap}
                      hostOverrides={hostOverrides}
                      clearOverride={clearHostOverride}
                    />
                  </Field>
                  <Field label={CONFIG_OVERRIDE_LABELS['stats.namespace']}>
                    <ConfigOverrideHelpText configOverride="stats.namespace" />
                    <Input
                      {...register('configOverrides.stats.namespace', { disabled: isLoading })}
                      className="width-100"
                      hasError={Boolean(errors.configOverrides?.stats?.namespace)}
                    />
                    <FieldFooter
                      name="configOverrides.stats.namespace"
                      overrideMap={overrideMap}
                      hostOverrides={hostOverrides}
                      clearOverride={clearHostOverride}
                    />
                  </Field>
                  <Field label={CONFIG_OVERRIDE_LABELS['stats.subsystem']}>
                    <ConfigOverrideHelpText configOverride="stats.subsystem" />
                    <Input
                      {...register('configOverrides.stats.subsystem', { disabled: isLoading })}
                      className="width-100"
                      hasError={Boolean(errors.configOverrides?.stats?.subsystem)}
                    />
                    <FieldFooter
                      name="configOverrides.stats.subsystem"
                      overrideMap={overrideMap}
                      hostOverrides={hostOverrides}
                      clearOverride={clearHostOverride}
                    />
                  </Field>
                </>
              : null}
              {statsType === 'graphite' ?
                <>
                  <Field label={CONFIG_OVERRIDE_LABELS['stats.host']}>
                    <ConfigOverrideHelpText configOverride="stats.host" />
                    <Input
                      {...register('configOverrides.stats.host', { disabled: isLoading })}
                      className="width-100"
                      hasError={Boolean(errors.configOverrides?.stats?.host)}
                    />
                    <FieldFooter
                      name="configOverrides.stats.host"
                      overrideMap={overrideMap}
                      hostOverrides={hostOverrides}
                      clearOverride={clearHostOverride}
                    />
                  </Field>
                  <Field label={CONFIG_OVERRIDE_LABELS['stats.prefix']}>
                    <ConfigOverrideHelpText configOverride="stats.prefix" />
                    <Input
                      {...register('configOverrides.stats.prefix', { disabled: isLoading })}
                      className="width-100"
                      hasError={Boolean(errors.configOverrides?.stats?.prefix)}
                    />
                    <FieldFooter
                      name="configOverrides.stats.prefix"
                      overrideMap={overrideMap}
                      hostOverrides={hostOverrides}
                      clearOverride={clearHostOverride}
                    />
                  </Field>
                </>
              : null}
              <Field label={CONFIG_OVERRIDE_LABELS['stats.interval']}>
                <ConfigOverrideHelpText configOverride="stats.interval" />
                <Input
                  {...register('configOverrides.stats.interval', { disabled: isLoading })}
                  className="width-100"
                  type="text"
                  max={255}
                  inputMode="numeric"
                  hasError={Boolean(errors.configOverrides?.stats?.interval)}
                />
                <FieldFooter
                  name="configOverrides.stats.interval"
                  overrideMap={overrideMap}
                  hostOverrides={hostOverrides}
                  clearOverride={clearHostOverride}
                />
              </Field>
              <SelectControlled
                label={CONFIG_OVERRIDE_LABELS['stats.lighthouse_metrics']}
                name="configOverrides.stats.lighthouse_metrics"
                control={control}
                disabled={Boolean(isLoading)}
                help={<ConfigOverrideHelpText configOverride="stats.lighthouse_metrics" />}
                hasError={Boolean(errors.configOverrides?.stats?.lighthouse_metrics)}
                fieldChildren={
                  <FieldFooter
                    name="configOverrides.stats.lighthouse_metrics"
                    overrideMap={overrideMap}
                    hostOverrides={hostOverrides}
                    clearOverride={clearHostOverride}
                  />
                }
                renderValue={(item) => item || null}
                renderOptions={({ onChange }) => (
                  <>
                    {ON_OFF.map((v) => (
                      <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                        {v}
                      </SelectItem>
                    ))}
                  </>
                )}
              />
              <SelectControlled
                label={CONFIG_OVERRIDE_LABELS['stats.message_metrics']}
                name="configOverrides.stats.message_metrics"
                control={control}
                disabled={Boolean(isLoading)}
                help={<ConfigOverrideHelpText configOverride="stats.message_metrics" />}
                hasError={Boolean(errors.configOverrides?.stats?.message_metrics)}
                fieldChildren={
                  <FieldFooter
                    name="configOverrides.stats.message_metrics"
                    overrideMap={overrideMap}
                    hostOverrides={hostOverrides}
                    clearOverride={clearHostOverride}
                  />
                }
                renderValue={(item) => item || null}
                renderOptions={({ onChange }) => (
                  <>
                    {ON_OFF.map((v) => (
                      <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                        {v}
                      </SelectItem>
                    ))}
                  </>
                )}
              />
            </>
          : null}
        </FieldSet>
        {unknownOverrides.length ?
          <>
            <Divider />
            <FieldSet legend="Unsupported config">
              <UnknownConfigOverridesList
                items={unknownOverrides}
                removedIndices={removedUnknownIndices}
                onRemove={formState.disabled ? undefined : removeUnknownOverride}
                onRestore={formState.disabled ? undefined : restoreUnknownOverride}
              />
            </FieldSet>
          </>
        : null}
      </div>

      {/* Add/Edit modals for lighthouse configs */}
      {isAddingRemoteAllow && (
        <RemoteAllowModal
          cidrValidation={makeUniqueCIDRRequiredValidator(currentRemoteCIDRs)}
          onSave={(values) => {
            appendRemoteAllow(values);
            setAddingRemoteAllow(false);
          }}
          onCancel={() => {
            setAddingRemoteAllow(false);
          }}
        />
      )}
      {editRemoteAllowIndex != null ?
        <RemoteAllowModal
          cidrValidation={makeUniqueCIDRRequiredValidator(currentRemoteCIDRs, editRemoteAllowIndex)}
          value={lighthouseRemoteAllows[editRemoteAllowIndex]}
          onSave={(values) => {
            updateRemoteAllow(editRemoteAllowIndex, values);
            setEditRemoteAllowIndex(null);
          }}
          onCancel={() => {
            setEditRemoteAllowIndex(null);
          }}
        />
      : null}
      {isAddingLocalAllow && (
        <LocalAllowModal
          cidrValidation={makeUniqueNameRequiredValidator(currentLocalCIDRs)}
          onSave={(values) => {
            appendLocalAllow(values);
            setAddingLocalAllow(false);
          }}
          onCancel={() => {
            setAddingLocalAllow(false);
          }}
        />
      )}
      {editLocalAllowIndex != null ?
        <LocalAllowModal
          cidrValidation={makeUniqueNameRequiredValidator(currentLocalCIDRs, editLocalAllowIndex)}
          value={lighthouseLocalAllows[editLocalAllowIndex]}
          onSave={(values) => {
            updateLocalAllow(editLocalAllowIndex, values);
            setEditLocalAllowIndex(null);
          }}
          onCancel={() => {
            setEditLocalAllowIndex(null);
          }}
        />
      : null}
    </>
  );
}
